import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTasks } from '../../context/TasksContext';
import { useCompany } from '../../context/CompanyContext';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
} from '../ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '../ui/button';
import { UserCheck, X, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

/**
 * JobAssignModal
 *
 * Assign (or reassign) a job task to a technician.
 * Queries company users filtered to role === 'technician'.
 * Writes assignedTo, assignedToName, assignedAt directly via TasksContext.
 *
 * Sprint 290: Rewritten on top of Radix Dialog (via shadcn/ui) so that when
 * this modal is opened from inside a Radix `<Sheet>` (WorkOrderSheet on
 * mobile), it participates in Radix's dismissable-layer stack. Previous
 * implementations that portalled a plain div to `document.body` were
 * visually correct but lost pointer hit-testing on iPhone because the
 * enclosing Sheet's dialog layer disables pointer events on body-siblings
 * while keeping SheetContent interactive.
 *
 * Props:
 *   isOpen  — boolean
 *   onClose — fn()
 *   task    — task document (must have task.id)
 */
const JobAssignModal = ({ isOpen, onClose, task }) => {
  const { updateTask } = useTasks();
  const { companyId } = useCompany();

  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [assigning, setAssigning]     = useState(null); // uid of currently-being-assigned tech

  // Load technicians from company membership + user profile
  useEffect(() => {
    if (!isOpen || !companyId) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // Read membership docs: companies/{companyId}/users
        const membersSnap = await getDocs(
          collection(db, 'companies', companyId, 'users')
        );

        const techMembers = membersSnap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((m) => m.role === 'technician');

        // Enrich with display names from /users/{uid}
        // Note: /users/{uid} is self-read only per Firestore rules, so profile
        // reads for OTHER members will silently fail. In that case we fall
        // back to the canonical `name` now written on the membership doc
        // (see iter_158 fix), then to email local-part, then to uid.
        const enriched = await Promise.all(
          techMembers.map(async (m) => {
            let profile = {};
            try {
              const profileSnap = await getDoc(doc(db, 'users', m.uid));
              if (profileSnap.exists()) profile = profileSnap.data();
            } catch {
              profile = {};
            }
            const email = profile.email || m.email || '';
            const displayName =
              profile.displayName ||
              profile.name ||
              m.name ||
              (email ? email.split('@')[0] : null) ||
              m.uid;
            return { uid: m.uid, displayName, email, role: 'technician' };
          })
        );

        if (!cancelled) {
          // Show "Unassigned" entry at top so users can remove an assignment
          setTechnicians([
            { uid: '__unassign__', displayName: 'Unassigned', email: '', role: '_unassign' },
            ...enriched,
          ]);
        }
      } catch (err) {
        console.error('[JobAssignModal] Failed to load technicians:', err);
        if (!cancelled) toast.error('Failed to load technicians');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, companyId]);

  const handleAssign = async (tech) => {
    if (!task?.id) return;
    setAssigning(tech.uid);
    try {
      if (tech.uid === '__unassign__') {
        await updateTask(task.id, {
          assignedTo:     null,
          assignedToName: null,
          assignedAt:     null,
        });
        toast.success('Assignment removed');
      } else {
        await updateTask(task.id, {
          assignedTo:     tech.uid,
          assignedToName: tech.displayName,
          assignedAt:     new Date().toISOString(),
        });
        toast.success(`Assigned to ${tech.displayName}`);
      }
      onClose();
    } catch (err) {
      console.error('[JobAssignModal] Assignment failed:', err);
      toast.error('Failed to update assignment');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => { if (!next) onClose(); }}
    >
      <DialogPortal>
        {/* Overlay z-[70] so it sits above Radix Sheet's default z-50 overlay,
            and above SheetContent (also z-50). The Sheet remains mounted
            underneath; only this dialog is interactive while open. */}
        <DialogOverlay
          className="z-[70] bg-black/50"
          data-testid="assign-modal-backdrop"
        />
        <DialogPrimitive.Content
          data-testid="job-assign-modal"
          className="fixed left-[50%] top-[50%] z-[71] w-80 max-w-[calc(100vw-2rem)]
                     -translate-x-1/2 -translate-y-1/2
                     bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden
                     focus:outline-none
                     data-[state=open]:animate-in data-[state=closed]:animate-out
                     data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
                     data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <DialogPrimitive.Title className="sr-only">
            Assign Technician
          </DialogPrimitive.Title>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">Assign Technician</h3>
            </div>
            <DialogPrimitive.Close
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-200 transition-colors"
              data-testid="assign-modal-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-500" />
            </DialogPrimitive.Close>
          </div>

          {/* Body */}
          <div className="p-2 max-h-72 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading technicians...</span>
              </div>
            ) : technicians.length <= 1 ? (
              // Only the "Unassigned" sentinel — no real technicians
              <div className="text-center py-8 text-sm text-gray-500 px-4">
                No technicians found in this company.
                <br />
                <span className="text-xs text-gray-400 mt-1 block">
                  Invite team members with the Technician role from Settings → Team Members.
                </span>
              </div>
            ) : (
              <ul className="space-y-0.5">
                {technicians.map((tech) => {
                  const isCurrent = task?.assignedTo === tech.uid;
                  const isUnassigning = tech.uid === '__unassign__';
                  const isProcessing = assigning === tech.uid;

                  return (
                    <li key={tech.uid}>
                      <button
                        type="button"
                        data-testid={`assign-tech-${tech.uid}`}
                        onClick={() => handleAssign(tech)}
                        disabled={!!assigning}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors
                          ${isCurrent
                            ? 'bg-orange-50 border border-orange-200'
                            : 'hover:bg-gray-50 border border-transparent'
                          }
                          ${isUnassigning ? 'text-gray-400' : 'text-gray-800'}`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0
                          ${isUnassigning ? 'bg-gray-100' : 'bg-orange-100'}`}>
                          {isUnassigning
                            ? <X className="w-3.5 h-3.5 text-gray-400" />
                            : <User className="w-3.5 h-3.5 text-orange-600" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{tech.displayName}</p>
                          {!isUnassigning && tech.email && (
                            <p className="text-xs text-gray-400 truncate">{tech.email}</p>
                          )}
                        </div>
                        {isCurrent && (
                          <span className="text-xs font-medium text-orange-600 shrink-0">Current</span>
                        )}
                        {isProcessing && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 shrink-0" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 pb-3 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-gray-600"
              onClick={onClose}
              data-testid="assign-modal-cancel"
            >
              Cancel
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default JobAssignModal;
